import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import FieldzoneModal from '../components/FieldzoneModal';
import { Environment } from '../constants/environments';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useLoaderScreenContext } from '../hooks/contexts/loader-screen.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useGuardian from '../hooks/useGuardian';
import * as Styles from '../styles/pages/payment';
import {
  trackAddPaymentInfo,
  trackBeginCheckout,
  trackPurchase,
} from '../trackings/analyticsDataLayer';
import { Checkout } from '../types/checkout';
import { logger } from '../utils/logger';

const PaymentPage: React.FC = () => {
  const posthog = usePostHog();

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [checkoutPaid, setCheckoutPaid] = useState(false);

  /**
   * MKT-683 — keeps `begin_checkout` to a single event per checkout. Reset when a
   * new iframe is mounted (a retry produces a fresh `iframeUrl`), otherwise the
   * second checkout attempt would silently emit nothing.
   */
  const beginCheckoutTracked = useRef(false);

  const { couponApplied, passKeyApplied } = useEventContext();

  const { render, navigateToNextRoute } = useGuardian();
  const {
    namingData,
    prepareCheckoutData,
    requestCheckoutPaymentUrl,
    summaryData,
    updateTransactionId,
    updatePaymentType,
    transactionId,
    paymentType,
    combosInNaming,
    prepareCheckoutComboData,
    cart,
    combosInCart,
  } = useCartContext();
  const { user } = useUserContext();
  const { event } = useEventContext();
  const { toggleLoader } = useLoaderScreenContext();
  const { currentLocale : { locale }, translate } = useI18nContext();
  const { clientUrl } = useParams();
  const { setContentModal, toggleModal } = useModalContext();

  /**
   * The gateway callbacks (`onPricing` / `onPaymentInfo`) are handed to
   * `checkout.mount` inside an effect keyed on `iframeUrl`, so they close over the
   * render that mounted the iframe. Reading the tracking inputs from a ref that is
   * refreshed on every render keeps the emitted events aligned with the current
   * cart/coupon instead of the snapshot taken at mount — same pattern already used
   * by `handleSuccessRef` in `resale-payment.tsx`.
   */
  const trackingInputsRef = useRef({
    event,
    summaryData,
    couponApplied,
    passKeyApplied,
    user,
    transactionId,
  });
  useEffect(() => {
    trackingInputsRef.current = {
      event,
      summaryData,
      couponApplied,
      passKeyApplied,
      user,
      transactionId,
    };
  });

  async function requestCheckoutUrl({
    checkoutData,
  }: {
    checkoutData: Checkout;
  }) {
    toggleLoader({ show : true });
    const gatewayDate = await requestCheckoutPaymentUrl(checkoutData);
    if (!gatewayDate) {
      toggleLoader({ show : false });
      return;
    }

    // Persist the id only once we know the request succeeded. `String(...)` on an
    // absent value used to store the literal 'undefined', which `trackPurchase`
    // rejects — silently dropping the analytics for a purchase that did go through.
    updateTransactionId(gatewayDate.transactionId ?? '');

    if (gatewayDate.checkoutUrl === 'approved') {
      toggleLoader({ show : false });
      navigateToNextRoute({ route : 'payment-confirmed' });
      return;
    }

    setIframeUrl(gatewayDate.checkoutUrl);
    logger.info(gatewayDate.checkoutUrl);
    toggleLoader({ show : false });
  }

  // MKT-258 / MKT-490 — analytics dataLayer + server-side Meta CAPI. GTM owns the
  // GA4 / Ads / Meta fan-out, so these are the sole client tracking calls.
  //
  // MKT-683 — both events moved out of `handleSuccess`: they used to fire only
  // after the payment was approved, so an abandoned checkout produced neither and
  // the three funnel events landed at once. They now follow the gateway callbacks,
  // and carry the amounts it priced instead of the cart estimate.
  const handleCheckoutPricing = (gatewayDetails: IPricingDetails) => {
    // The gateway re-prices on every change of method or installment plan; only the
    // first snapshot is the customer reaching the payment step.
    if (beginCheckoutTracked.current) return;
    beginCheckoutTracked.current = true;

    trackBeginCheckout({ ...trackingInputsRef.current, gatewayDetails });
  };

  const handleCheckoutPaymentInfo = (gatewayDetails: IPricingDetails) => {
    trackAddPaymentInfo({
      ...trackingInputsRef.current,
      paymentType : gatewayDetails.type,
      gatewayDetails,
    });
  };

  const handleAnalyticsTrackingCheckoutPaid = (
    paymentMethod?: string,
    gatewayDetails?: IPaymentDetails,
  ) => {
    // Like the two callbacks above, this one is handed to `checkout.mount` inside
    // the `iframeUrl` effect, so reading the cart/coupon/event straight from the
    // component would pin them to the render that mounted the iframe.
    const trackingInputs = trackingInputsRef.current;

    // Prevent analytics tracking if summaryData is empty
    if (!trackingInputs.summaryData || trackingInputs.summaryData.length === 0) {
      logger.warn('Cannot track analytics: summaryData is empty');
      return;
    }

    try {
      posthog.capture('purchase:success_page_view');

      // MKT-258 / MKT-490 — analytics dataLayer purchase + server-side Meta CAPI.
      // Deduped by transaction_id so payment-confirmed.tsx cannot double-fire it,
      // and the CAPI reuses that transaction_id as the Meta dedup id (MKT-484).
      //
      // Spreading the whole ref is what keeps `passKeyApplied` on the event: the
      // dedup makes `purchase` a ONE-SHOT event, so a field missing here cannot be
      // repaired by payment-confirmed.tsx later — its call is discarded. Omitting
      // it used to ship the conversion without `discount_code` while every earlier
      // funnel event carried it.
      trackPurchase({
        ...trackingInputs,
        paymentType : paymentMethod,
        affiliation : clientUrl,
        // MKT-683 — the amounts the gateway actually charged (currency, coupon,
        // fees and the interest of the installment plan chosen inside the iframe).
        gatewayDetails,
      });
    } catch (error) {
      logger.error(`Failed to track checkout paid analytics: ${error}`);
    }
  };

  const showFieldzoneModal = () => {
    setContentModal(<FieldzoneModal />);
    toggleModal({ open : true });
  };

  // Show Fielzone modal for events with "Fielzone" category
  const handleFielzoneModal = () => {
    const hasFieldzoneCategory = event?.categories?.some(
      (category) => category.name?.toLowerCase().includes('fielzone')
    );

    if (event && hasFieldzoneCategory) {
      showFieldzoneModal();
    }
  };

  useEffect(() => {
    if (!user || !event) return;

    // Clear transactionId when accessing payment page to allow new transactions
    if (transactionId) {
      updateTransactionId('');
    }

    // MKT-258 — and clear the persisted payment method with it. `updatePaymentType`
    // is only written from `handleSuccess` (the iframe's `onSuccess`), which the
    // instant-approval path never reaches: `requestCheckoutUrl` returns on
    // `checkoutUrl === 'approved'` before the iframe mounts. Without this reset a
    // method left over from an earlier attempt in the same session would be
    // attached to that purchase — `resetCart(true)` preserves it too. The gateway
    // reports no method on that path (`GatewayDetails` has none), so the correct
    // outcome is an ABSENT `payment_type`, never a stale one.
    if (paymentType) {
      updatePaymentType(null);
    }

    // Prevent duplicate transactions if cart is empty (likely after successful purchase)
    if (cart.length === 0 && combosInCart.length === 0) {
      logger.warn('Cart is empty, preventing duplicate transaction request. Redirecting to tickets page.');
      navigateToNextRoute({ route : 'tickets' });
      return;
    }

    const data = prepareCheckoutData({ namingData, user, event });
    const comboData =
      prepareCheckoutComboData({
        comboNamingData : combosInNaming,
        user,
        event,
      }) ?? [];
    if (!data) return;

    let definitiveData = data;
    if (comboData) {
      definitiveData = {
        ...definitiveData,
        tickets : [...data.tickets, ...comboData.tickets],
      };
    }

    requestCheckoutUrl({ checkoutData : definitiveData });
  }, [user, event, namingData, combosInNaming, cart, combosInCart]);

  useEffect(() => {
    if (!iframeUrl) return;

    // A new iframe is a new checkout attempt — let it emit its own `begin_checkout`.
    beginCheckoutTracked.current = false;

    const checkout = window.IngresseCheckout;

    checkout?.mount('checkout-frame', iframeUrl, {
      // Canonical BCP-47, straight through: the gateway normalises separator
      // and case itself and ships pt_PT. See the test for what was verified.
      locale : locale,
      onSuccess : (eventDetails) => {
        handleSuccess(eventDetails);
      },
      onTimeout : () => {
        // handleTimeout();
      },
      onPricing : handleCheckoutPricing,
      onPaymentInfo : handleCheckoutPaymentInfo,
      style : {
        textColorPrimary : '212B36',
        textColorSecondary : '454F5B',
        textColorHighlight : '2280EF',
        textColorAccent : 'FF271A',

        bgColorPrimary : 'F4F6F8',
        bgColorSecondary : 'F9FAFB',
        bgColorAccent : 'FF271A14',

        borderColorPrimary : 'C4CDD5',
        borderColorAccent : 'C81F14',

        buttonColorPrimary : 'FF271A',
        buttonColorHover : 'C81F14',

        font : 'Elza',
      },
    });
  }, [iframeUrl]);

  useEffect(() => {
    return () => {
      if (window.IngresseCheckout) {
        window.IngresseCheckout.cancel();
      }
    };
  }, []);

  const handleSuccess = (gatewayDetails: IPaymentDetails) => {
    const { status, type } = gatewayDetails;

    // MKT-258 — persist the payment method so payment-confirmed.tsx can attach
    // it to the analytics purchase on the instant-approval path (no iframe there).
    updatePaymentType(type ?? null);

    if (status === 'pending') {
      navigateToNextRoute({ route : 'payment-in-process' });
      return;
    }

    if (status === 'paid' || status === 'authorized' || status === 'partially paid') {
      setCheckoutPaid(true);
      handleAnalyticsTrackingCheckoutPaid(type, gatewayDetails);
      if (status === 'paid') {
        handleFielzoneModal();
      }
    }
  };

  if (!iframeUrl) return null;

  return render(
    <>
      <Styles.CheckoutContainer id="checkout-frame"></Styles.CheckoutContainer>
      {checkoutPaid && (
        <>
          <Styles.PaymentFooter>
            <Styles.CustomBigButtonOutlined
              id='show-events'
              type='button'
              onClick={() => {
                posthog.capture('purchase:success_page_show_events_click');
                window.location.href = Environment.SITE_URL;
              }}
            >
              {translate(['INFORMATION', 'SEE_MORE_EVENTS'])}
            </Styles.CustomBigButtonOutlined>
            <Styles.CustomBigButton
              id='show-wallet'
              type='button'
              onClick={() => {
                posthog.capture('purchase:success_page_show_wallet_click');
                window.location.href = Environment.WALLET_URL;
              }}
            >
              {translate(['PAYMENT_STATUS.TEXT', 'PT_50'])}
            </Styles.CustomBigButton>
          </Styles.PaymentFooter>
        </>
      )}
    </>
  );
};

export default PaymentPage;
